import { BaseButton } from "components/buttons/BaseButton";
import { CircularProgress } from "@mui/material";
import { useEffect, useCallback, useState } from "react";
import UploadMoveBack from "../UploadMoveBack";
import { IFile } from "./UploadSteps";
import { useNavigate, Link } from "react-router-dom";
import { routes } from "routes";
import { createGifs } from "utils/gifUploader";
import { copyLink } from "utils/share";
import { toast } from "react-hot-toast";
import { Popup } from "components/UI/Popup";
import { ReactComponent as ShareIcon } from "components/icons/share.svg";
import "./styles/UploadBulkImgStep.scss";
import { clsx } from "clsx";

interface IUploadBulkImgStepProps {
  back: () => void;
  setFiles: (files: any) => void;
  files: IFile[];
}

export const UploadBulkImgStep = (props: IUploadBulkImgStepProps) => {
  const { back, setFiles, files } = props;
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!files.length) {
      return;
    }

    const fetchData = async () => {
      try {
        await createGifs(files, (sent: any) => {
          setFiles((prevFiles: any) => {
            // Create a new copy of the files array
            const updatedFiles = [...prevFiles];

            updatedFiles[sent.index].ticketId = sent.gifId;

            if (sent.status !== "complete") {
              updatedFiles[sent.index].progress = true;
            } else {
              updatedFiles[sent.index].progress = false;
            }

            // Return the updated files array
            return updatedFiles;
          });
        });
      } catch (err) {
        toast.error("Something went wrong. Please try later", { duration: 5000 });
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoToManage = () => navigate(routes.manageProfile("i"));

  const handleCopyLink = useCallback((ticketId: string | undefined) => {
    copyLink(`https://i.redgifs.com/i/${ticketId}.jpg`);
  }, []);

  const handleTogglePopup = () => setShowPopup(prev => !prev);

  const renderImageBlock = (el: IFile) => {
    return (
      <div className="ImageBlock" key={el.id}>
        <Link
          className={clsx(
            "ImageBlock-ImgTitle",
            !(el.progress === undefined || el.progress || !el.ticketId) && "ImageBlock-ImgTitle_isReady"
          )}
          target="_blank"
          to={routes.watch(el.ticketId || "")}
        >
          <img src={el.url} alt={el.file.name} />
          <p className="title">{el.file.name}</p>
        </Link>
        <div className="ImageBlock-Btn">
          {el.progress === undefined || el.progress ? (
            <CircularProgress
              sx={{ width: "30px!important", height: "30px!important", color: "#8F2CF2" }}
            />
          ) : (
            <button
              onClick={() => handleCopyLink(el.ticketId)}
              disabled={!el?.ticketId}
              title={!el?.ticketId ? "uploading" : "Share link"}
            ><ShareIcon /></button>
          )}
        </div>
      </div>
    );
  };

  const btnProps = {
    type: 1,
    title: "Manage content",
    onClick: handleGoToManage
  };

  return (
    <div className="UploadBulkImgStep">
      <UploadMoveBack
        back={back}
        text="Bulk Upload Summary"
        setFiles={setFiles}
      />
      <p className="UploadBulkImgStep-Description">
        Once uploaded, the embed link is available via the share button on each individual piece of content
      </p>
      <div className="UploadBulkImgStep-Images">
        {files.map(renderImageBlock)}
      </div>
      <BaseButton onClick={handleTogglePopup}>Manage content</BaseButton>
      <Popup
        isOpen={showPopup}
        popupClass="GoManagePopup"
        btnProps={btnProps}
      >
        Uploads may still be processing. Once complete all bulk uploaded images will appear in your Manage Section.
      </Popup>
    </div>
  );
};
